// ABOUTME: React Query hook for fetching quantity configuration from the backend API.
// ABOUTME: Calls GET /tuc-configs/quantity, finds config for selected scope, and transforms to form-ready shape.

import {useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'

import {api} from '../../../../App.store'
import {
  FullLoadOptionsData,
  QuantityApiResponse,
  QuantityConfig,
  QuantityOverrides,
  QuantityValues,
} from '../types/quantity'

const EMPTY_OPTIONS: FullLoadOptionsData = {defaultNumberOfTrucks: 0, options: []}

const NO_OVERRIDES: QuantityOverrides = {
  uom: false,
  default: false,
  min: false,
  max: false,
  maxOrderConfirmable: false,
  step: false,
  shortLoadThreshold: false,
  numberOfDecimals: false,
  roundToChangeIncrement: false,
  fullLoadsEnabled: false,
  fullLoadsUom: false,
  fullLoadsOptions: false,
  loadBalanceEnabled: false,
  minEligibleQuantity: false,
  maxEligibleQuantity: false,
}

const extractValues = (response: QuantityApiResponse): QuantityValues => {
  const q = response.quantity ?? {}
  const fl = response.fullLoads ?? {}
  const lb = response.loadBalance ?? {}
  return {
    uom: q.uom?.value?.displaySymbol ?? '',
    uomId: q.uom?.value?.id,
    default: q.default?.value ?? 0,
    min: q.min?.value ?? 0,
    max: q.max?.value ?? 0,
    maxOrderConfirmable: q.maxOrderConfirmable?.value ?? 0,
    step: q.step?.value ?? 0,
    shortLoadThreshold: q.shortLoadThreshold?.value ?? 0,
    numberOfDecimals: q.numberOfDecimals?.value ?? 0,
    roundToChangeIncrement: q.roundToChangeIncrement?.value ?? false,
    fullLoadsEnabled: fl.isEnabled?.value ?? false,
    fullLoadsUom: fl.quantityUom?.value?.displaySymbol ?? '',
    fullLoadsUomId: fl.quantityUom?.value?.id,
    fullLoadOptions: fl.options?.value ?? EMPTY_OPTIONS,
    loadBalanceEnabled: lb.isEnabled?.value ?? false,
    minEligibleQuantity: lb.minEligibleQuantity?.value ?? 0,
    maxEligibleQuantity: lb.maxEligibleQuantity?.value ?? 0,
  }
}

const extractOverrides = (response: QuantityApiResponse): QuantityOverrides => {
  const q = response.quantity ?? {}
  const fl = response.fullLoads ?? {}
  const lb = response.loadBalance ?? {}
  return {
    uom: q.uom?.isOverridden ?? false,
    default: q.default?.isOverridden ?? false,
    min: q.min?.isOverridden ?? false,
    max: q.max?.isOverridden ?? false,
    maxOrderConfirmable: q.maxOrderConfirmable?.isOverridden ?? false,
    step: q.step?.isOverridden ?? false,
    shortLoadThreshold: q.shortLoadThreshold?.isOverridden ?? false,
    numberOfDecimals: q.numberOfDecimals?.isOverridden ?? false,
    roundToChangeIncrement: q.roundToChangeIncrement?.isOverridden ?? false,
    fullLoadsEnabled: fl.isEnabled?.isOverridden ?? false,
    fullLoadsUom: fl.quantityUom?.isOverridden ?? false,
    fullLoadsOptions: fl.options?.isOverridden ?? false,
    loadBalanceEnabled: lb.isEnabled?.isOverridden ?? false,
    minEligibleQuantity: lb.minEligibleQuantity?.isOverridden ?? false,
    maxEligibleQuantity: lb.maxEligibleQuantity?.isOverridden ?? false,
  }
}

const hasAnyOverride = (overrides: QuantityOverrides): boolean =>
  Object.values(overrides).some(Boolean)

/**
 * Resolves the values shown for a scope: each field that is NOT overridden at this scope inherits the
 * parent (root) value, while overridden fields keep their own value. Without this, fields absent from
 * the API response (i.e. inherited) map to empty defaults that look like local overrides and get sent
 * on save with invalid empty/zero/null values. The `uom` flag gates both `uom` and `uomId`; the
 * `fullLoadsUom` flag gates both `fullLoadsUom` and `fullLoadsUomId`; `fullLoadsOptions` gates the
 * options object.
 */
const resolveInheritedValues = (
  own: QuantityValues,
  overrides: QuantityOverrides,
  parent: QuantityValues
): QuantityValues => ({
  uom: overrides.uom ? own.uom : parent.uom,
  uomId: overrides.uom ? own.uomId : parent.uomId,
  default: overrides.default ? own.default : parent.default,
  min: overrides.min ? own.min : parent.min,
  max: overrides.max ? own.max : parent.max,
  maxOrderConfirmable: overrides.maxOrderConfirmable
    ? own.maxOrderConfirmable
    : parent.maxOrderConfirmable,
  step: overrides.step ? own.step : parent.step,
  shortLoadThreshold: overrides.shortLoadThreshold
    ? own.shortLoadThreshold
    : parent.shortLoadThreshold,
  numberOfDecimals: overrides.numberOfDecimals ? own.numberOfDecimals : parent.numberOfDecimals,
  roundToChangeIncrement: overrides.roundToChangeIncrement
    ? own.roundToChangeIncrement
    : parent.roundToChangeIncrement,
  fullLoadsEnabled: overrides.fullLoadsEnabled ? own.fullLoadsEnabled : parent.fullLoadsEnabled,
  fullLoadsUom: overrides.fullLoadsUom ? own.fullLoadsUom : parent.fullLoadsUom,
  fullLoadsUomId: overrides.fullLoadsUom ? own.fullLoadsUomId : parent.fullLoadsUomId,
  fullLoadOptions: overrides.fullLoadsOptions ? own.fullLoadOptions : parent.fullLoadOptions,
  loadBalanceEnabled: overrides.loadBalanceEnabled
    ? own.loadBalanceEnabled
    : parent.loadBalanceEnabled,
  minEligibleQuantity: overrides.minEligibleQuantity
    ? own.minEligibleQuantity
    : parent.minEligibleQuantity,
  maxEligibleQuantity: overrides.maxEligibleQuantity
    ? own.maxEligibleQuantity
    : parent.maxEligibleQuantity,
})

const transformResponse = (
  response: QuantityApiResponse,
  parent?: QuantityValues
): QuantityConfig => {
  const overrides = extractOverrides(response)
  const own = extractValues(response)
  const values = parent ? resolveInheritedValues(own, overrides, parent) : own
  return {
    id: response.id,
    ...values,
    overrides,
    parentValues: parent ?? own,
    _meta: {
      scopeId: response.scopeId,
      isInherited: !hasAnyOverride(overrides),
      resolvedFrom: null,
      createdAt: response._meta.createdAt,
      updatedAt: response._meta.updatedAt,
      createdBy: response._meta.createdBy,
      updatedBy: response._meta.updatedBy,
    },
  }
}

const emptyConfig = (scopeId: string): QuantityConfig => {
  const values: QuantityValues = {
    uom: '',
    uomId: undefined,
    default: 0,
    min: 0,
    max: 0,
    maxOrderConfirmable: 0,
    step: 0,
    shortLoadThreshold: 0,
    numberOfDecimals: 0,
    roundToChangeIncrement: false,
    fullLoadsEnabled: false,
    fullLoadsUom: '',
    fullLoadsUomId: undefined,
    fullLoadOptions: {defaultNumberOfTrucks: 0, options: []},
    loadBalanceEnabled: false,
    minEligibleQuantity: 0,
    maxEligibleQuantity: 0,
  }
  return {
    id: undefined,
    ...values,
    overrides: {...NO_OVERRIDES},
    parentValues: values,
    _meta: {
      scopeId,
      isInherited: false,
      resolvedFrom: null,
      createdAt: '',
      updatedAt: '',
      createdBy: '',
      updatedBy: '',
    },
  }
}

export const useQuantityConfig = (
  countryId: string | undefined,
  businessLine: string | undefined,
  scopeId: string | undefined,
  rootScopeId?: string | undefined
): {data: QuantityConfig | undefined; isLoading: boolean; error: Error | null} => {
  const {data: rawData, isLoading, error} = useQuery<QuantityApiResponse[], Error>({
    queryKey: ['quantityConfig', countryId, businessLine],
    queryFn: async () => {
      const res = await api.get<QuantityApiResponse[]>('/tuc-configs/quantity', {
        params: {countryId, businessLine: businessLine!.toUpperCase()},
      })
      return res.data ?? []
    },
    enabled: Boolean(countryId && businessLine && scopeId),
    staleTime: 30_000,
  })

  const data = useMemo(() => {
    if (!rawData || !scopeId) return undefined

    const rootRow = rootScopeId ? rawData.find((r) => r.scopeId === rootScopeId) : rawData[0]
    const parentValues = rootRow ? extractValues(rootRow) : undefined

    const match = rawData.find((r) => r.scopeId === scopeId)
    if (match) {
      // Resolve non-overridden fields from the parent (root) so the form shows inherited values and
      // only genuine overrides are detected/sent. Falling back to own values when no parent exists.
      return transformResponse(match, parentValues)
    }

    // No row for the selected scope, but a parent/root config exists → inherit from it.
    if (rootRow) {
      const inherited = transformResponse(rootRow)
      return {
        ...inherited,
        id: undefined,
        _meta: {...inherited._meta, isInherited: true},
        overrides: {...NO_OVERRIDES},
        parentValues: parentValues ?? inherited.parentValues,
      }
    }

    // No rows at all → render an empty, creatable config (e.g. brand-new country default / 404).
    return emptyConfig(scopeId)
  }, [rawData, scopeId, rootScopeId])

  return {data, isLoading, error}
}
